const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * Renders a countdown timer to a target date/time.
 * Matches markup: `<count-down data-countdown-end="YYYY-MM-DDTHH:MM:SS"></count-down>`
 */
class CountDown extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;

  /** @type {number} */
  #endTime = 0;

  /** @type {string} */
  #endDate = this.getAttribute('data-countdown-end');

  connectedCallback() {
    if (!this.#endDate) {
      return;
    }

    try {
      this.#endTime = new Date(this.#endDate).getTime();
      this.#startCountdown();
    } catch (error) {
      console.error('Error parsing countdown end date:', error);
    }
  }

  disconnectedCallback() {
    if (this.#interval !== null) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  /**
   * Calculates the time remaining until the end date.
   * @returns {{ days: number, hours: number, minutes: number, seconds: number }} */
  #getTimeRemaining() {
    const ms = Math.max(0, this.#endTime - Date.now());

    return {
      days: Math.floor(ms / DAY),
      hours: Math.floor((ms % DAY) / HOUR),
      minutes: Math.floor((ms % HOUR) / MINUTE),
      seconds: Math.floor((ms % MINUTE) / SECOND),
    };
  }

  /**
   * Sets the text content of an element.
   * @param {string} selector - The selector of the element to set the text content of.
   * @param {string} value - The value to set the text content to.
   */
  #setTextContent(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  /**
   * Updates the display of the countdown timer.
   */
  #updateDisplay() {
    const { days, hours, minutes, seconds } = this.#getTimeRemaining();
    this.#setTextContent('[data-countdown-timer-days]', `${days}`);
    this.#setTextContent('[data-countdown-timer-hrs]', `${hours}`);
    this.#setTextContent('[data-countdown-timer-min]', `${minutes}`);
    this.#setTextContent('[data-countdown-timer-sec]', `${seconds}`);

    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  /**
   * Starts the countdown timer.
   */
  #startCountdown() {
    this.#updateDisplay();
    this.#interval = setInterval(this.#updateDisplay.bind(this), 1000);
  }
}

if (!customElements.get('count-down')) {
  customElements.define('count-down', CountDown);
}
